/**
 * @description: https://bytedance.feishu.cn/wiki/JVfAwkI0hiq1VGknawYcYdOdnxd
 * @author wuyan.geass
 */
// @ts-expect-error @jupiter/plugin-runtime is inject by main app
import runtimeI18n, { i18n as JupiterI18n } from '@jupiter/plugin-runtime/i18n';
import ICU from 'i18next-icu';
import { I18nPluginFactory } from '@i18n-ecom-alliance/lib-sdk';
import { i18nspect } from './i18nspect';

export interface ILocaleMap {
  i18nKey: string;
  defaultText: string;
}

type IParams = (string | number)[] | { [key: string]: string | number };
class I18n {
  constructor() {
    I18nPluginFactory.registerI18nPlugin(
      this.origin,
      I18nPluginFactory.multiCountryPlugin,
    );
    I18nPluginFactory.registerI18nPlugin(this.origin, ICU);
    I18nPluginFactory.registerI18nPlugin(this.origin, i18nspect);
  }

  get origin() {
    return JupiterI18n;
  }

  t = <T extends string | ILocaleMap>(
    key: string | ILocaleMap,
    defaultValue?: T extends string ? string : IParams,
    params?: T extends string ? IParams : any,
    options?: any,
  ) => {
    const { count, ...restOptions } = options || {};
    if (typeof key === 'string') {
      // show i18nKey
      if ((window as any).AghanimGlobalShowI18nKey) {
        return this.formatPlural(key, count);
      }
      const data = JupiterI18n.t(this.formatPlural(key, count), {
        defaultValue,
        ...params,
        ...restOptions,
      });
      return this.i18nFormat(data, params);
    }
    const { i18nKey, defaultText } = key ?? {};
    // show i18nKey
    if ((window as any).AghanimGlobalShowI18nKey) {
      return this.formatPlural(i18nKey, count);
    }
    const data = JupiterI18n.t(this.formatPlural(i18nKey, count), {
      defaultValue: defaultText,
      ...params,
      ...restOptions,
    });
    return this.i18nFormat(data, defaultValue as IParams);
  };

  changeLanguage = (params?: { locale?: string }) =>
    runtimeI18n.changeLanguage({ locale: params?.locale || 'en' });

  i18nFormat = (
    msg: string,
    param?: (string | number)[] | { [key: string]: string | number },
  ) => {
    if (!param) {
      return msg;
    }
    let computedMsg = msg;
    // use ['str1', 'str2'] to replace '%s', '%s' or '%d', '%d'
    if (Array.isArray(param)) {
      const replaceReg = /%(s|d)/gi;
      if (param.length === 1) {
        computedMsg = computedMsg.replace(replaceReg, () =>
          param[0].toString(),
        );
      } else if (param.length > 1) {
        let idx = 0;
        computedMsg = computedMsg.replace(replaceReg, () =>
          param[idx++].toString(),
        );
      }
    } else {
      // use { name: 'Bob', age: 18 } to replace 'MyName: {name}, MyAge: {age}'
      if (typeof param !== 'object') {
        console.warn("intl.format's param should be array or object");
        return computedMsg;
      }
      // first match double {}, use { name: 'Bob', age: 18 } to replace 'MyName: {{name}}, MyAge: {{age}}'
      computedMsg = computedMsg.replace(
        /{{(\w+)}}/gi,
        ($0, $1) => param[$1]?.toString() ?? $0,
      );
      computedMsg = computedMsg.replace(
        /{(\w+)}/gi,
        ($0, $1) => param[$1]?.toString() ?? $0,
      );
    }

    return computedMsg;
  };

  /**
   * plural spec
   * https://bytedance.feishu.cn/docs/doccnXcxYvOdMaOA2crZN286ahg
   */
  formatPlural(key: string, count?: number) {
    if (count === undefined) {
      return key;
    }
    if (key.endsWith('_singular') || key.endsWith('_plural')) {
      return key;
    }
    if (count === 1) {
      return `${key}_singular`;
    }
    return `${key}_plural`;
  }

  updateRegion(region: string) {
    I18nPluginFactory.updateMultiCountryRegion(region);
  }
}
const i18n = new I18n();

export default i18n;
export type I18nType = typeof i18n;
